import React, { useState, useCallback, useEffect } from "react";
import Video from "twilio-video";
import Room from "./room";
import { getTwilioToken } from "../appointments/create/api";
import { updateTelePatientDetails } from "../appointments/create/api/index";

// update log
// FB 6036 MS   05/17/2022    Created
// FB 6264 DK   11/22/2022    Migrate PE API,DB to EMR
// EMR-139 GA   02/02/2025    Permission issues

const VideoChat = (props) => {
    const [room, setRoom] = useState(null);
    const [connecting, setConnecting] = useState(false);
    const [permissionError, setPermissionError] = useState(null); // EMR-139
    const [permissionStatus, setPermissionStatus] = useState("checking");// EMR-139

    const username = props.match.params.patient;
    const roomName = props.match.params.room;
    // EMR-139 Begin
    const checkPermissions = async () => {
        try {
            // Check if permissions API is available
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const cameraPermission = await navigator.permissions.query({ name: 'camera' });
                    const micPermission = await navigator.permissions.query({ name: 'microphone' });
                    
                    if (cameraPermission.state === 'denied' || micPermission.state === 'denied') {
                        setPermissionStatus("denied");
                        setPermissionError("Camera or microphone access was denied. Please enable permissions in your browser settings and reload the page.");
                        return false;
                    }
                } catch (permErr) {
                    // Permissions API might not support camera/microphone on some browsers
                    console.log("Permissions API not fully supported, will try direct access");
                }
            }

            // Try to get user media to trigger permission prompt
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
                // Stop the stream immediately as we just needed to check/request permissions
                stream.getTracks().forEach(track => track.stop());
                setPermissionStatus("granted");
                return true;
            } catch (mediaErr) {
                console.error("Media access error:", mediaErr);
                if (mediaErr.name === 'NotAllowedError' || mediaErr.name === 'PermissionDeniedError') {
                    setPermissionStatus("denied");
                    setPermissionError("Camera and microphone access are required for video calls. Please enable permissions in your browser settings and reload the page.");
                } else if (mediaErr.name === 'NotFoundError') {
                    setPermissionStatus("denied");
                    setPermissionError("No camera or microphone found on your device.");
                } else {
                    setPermissionStatus("denied");
                    setPermissionError("Unable to access camera or microphone. Please check your device settings.");
                }
                return false;
            }
        } catch (err) {
            console.error("Permission check error:", err);
            setPermissionStatus("denied");
            setPermissionError("Unable to check permissions. Please ensure your browser supports video calls.");
            return false;
        }
    };

    useEffect(() => {
        async function initializeVideoCall() {
            const hasPermissions = await checkPermissions();
            
            if (!hasPermissions) {
                setConnecting(false);
                return;
            }

            try {
                const response = await getTwilioToken(username, roomName);
                Video.connect(response, {
                    name: roomName,
                })
                    .then((room) => {
                        setConnecting(false);
                        setRoom(room);
                        setPermissionError(null);
                    })
                    .catch((err) => {
                        console.error(err);
                        setConnecting(false);
                        if (err.name === 'NotAllowedError') {
                            setPermissionStatus("denied");
                            setPermissionError("Camera and microphone access are required. Please enable permissions and reload the page.");
                        }
                    });
            } catch (err) {
                console.error("Failed to get Twilio token:", err);
                setConnecting(false);
            }
        }

        initializeVideoCall();
    }, []);
    // EMR-139 End

    const handleLogout = useCallback(() => {
        setRoom((prevRoom) => {
            if (prevRoom) {
                prevRoom.localParticipant.tracks.forEach((trackPub) => {
                    trackPub.track.stop();
                });
                prevRoom.disconnect();
                updateTelePatientDetails(0, roomName);
            }
            return null;
        });
    }, []);

    useEffect(() => {
        if (room) {
            const tidyUp = (event) => {
                if (event.persisted) {
                    return;
                }
                if (room) {
                    handleLogout();
                }
            };
            window.addEventListener("pagehide", tidyUp);
            window.addEventListener("beforeunload", tidyUp);
            return () => {
                window.removeEventListener("pagehide", tidyUp);
                window.removeEventListener("beforeunload", tidyUp);
            };
        }
    }, [room, handleLogout]);

    const scrollTo = (ref) => {
        if (ref) {
            ref.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };
    // EMR-139 Begin
    const handleRetryPermissions = () => {
        setPermissionError(null);
        setPermissionStatus("checking");
        window.location.reload();
    };

    let render;
    if (permissionError) {
        render = (
            <div style={{
                padding: "20px",
                margin: "20px",
                backgroundColor: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: "8px",
                textAlign: "center"
            }}>
                <h3 style={{ color: "#856404", marginBottom: "15px" }}>Permission Required</h3>
                <p style={{ color: "#856404", marginBottom: "15px" }}>{permissionError}</p>
                <div style={{ marginBottom: "15px" }}>
                    <strong style={{ color: "#856404" }}>How to enable permissions:</strong>
                    <div style={{ marginTop: "10px", textAlign: "left", maxWidth: "600px", margin: "10px auto" }}>
                        <p style={{ color: "#856404" }}><strong>Android Chrome:</strong></p>
                        <ol style={{ color: "#856404", paddingLeft: "20px" }}>
                            <li>Tap the lock icon or info icon (ⓘ) in the address bar</li>
                            <li>Tap "Permissions" or "Site settings"</li>
                            <li>Enable "Camera" and "Microphone"</li>
                            <li>Reload this page</li>
                        </ol>
                        <p style={{ color: "#856404", marginTop: "10px" }}><strong>iOS Safari:</strong></p>
                        <ol style={{ color: "#856404", paddingLeft: "20px" }}>
                            <li>Go to Settings → Safari → Camera/Microphone</li>
                            <li>Set to "Ask" or "Allow"</li>
                            <li>Reload this page</li>
                        </ol>
                    </div>
                </div>
                <button
                    onClick={handleRetryPermissions}
                    style={{
                        backgroundColor: "#18a2b8",
                        color: "white",
                        padding: "10px 20px",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                        fontSize: "16px"
                    }}
                >
                    I've Enabled Permissions - Retry
                </button>
            </div>
        );
    } else if (room) {
        render = (
            <section
                ref={scrollTo}
                style={{
                    margin: "20px 20px 20px",
                }}
            >
                <Room
                    roomName={roomName}
                    identity={username}
                    room={room}
                    handleLogout={handleLogout} />
            </section>
        );
    } else {
        render = (
            <div style={{ textAlign: "center", padding: "20px" }}>
                <p>Connecting to video call...</p>
            </div>
        );
    }
    return render;
};
// EMR-139 End
export default VideoChat;
